import { Close, LocationOn, Menu } from 'emotion-icons/material'
import React, { useState } from 'react'

import { HEADER } from '../../utils/dataTrackingValues'

import Link from '../Link'
import * as Styled from './Header.styles'
import MobileNav from './MobileNav'

import RenderNavItems from './RenderNavItems'

const Header = ({
  requestId,
  navData = undefined,
  topBarNavData = null,
  userLocation = null,
  hideLocation = false,
  reverseTopBar = false,
  logo = null,
  logoLink = null,
  children = null,
  navigationPosition = null,
  navigationBackgroundColor = null,
  navigationTextColor = null,
  headerBackgroundColor = null,
  headerTextColor = null,
  topBarBackgroundColor = null,
  topBarTextColor = null,
  hoverBackgroundColor = null,
  hoverTextColor = null,
  borderColor = null,
  borderHeight = null,
  textBlock = null,
  textBlockAlignment = null,
  callToActions,
  topBarText = null,
  navigationVisibility = null,
  ...props
}) => {
  const headerBgColor = headerBackgroundColor?.toLowerCase()
  const headerColor = headerTextColor?.toLowerCase()
  const topBarBgColor = topBarBackgroundColor?.toLowerCase()
  const topBarColor = topBarTextColor?.toLowerCase()
  const navigationBgColor = navigationBackgroundColor?.toLowerCase()
  const navigationColor = navigationTextColor?.toLowerCase()
  const hoverBgColor = hoverBackgroundColor?.toLowerCase()
  const hoverColor = hoverTextColor?.toLowerCase()
  const border = borderColor?.toLowerCase()
  const hasTextBlock = textBlock?.props?.children.length > 0 || textBlock
  const [isMobileNavOpen, setMobileNavOpen] = useState(false)
  const navUnderLogo = navigationPosition === 'Under logo'
  // This allows us to pass gatsby-link down from gatsby-theme-leshen
  const getMobileMenuButton = () => {
    if (typeof navData === 'undefined' || !navData?.length > 0) {
      return null
    }

    if (isMobileNavOpen) {
      return <Close size={32} onClick={() => setMobileNavOpen(false)} />
    }

    return <Menu size={32} onClick={() => setMobileNavOpen(true)} />
  }

  const showTopBar = topBarNavData?.length > 0 || !hideLocation || topBarText

  const renderCallToActions = () => {
    if (callToActions && callToActions.length > 0) {
      return (
        <Styled.CallToActions textBlock={hasTextBlock}>
          {callToActions.map((action, index) => (
            <span key={action?.key || index}>{action}</span> // action.key doesn't exist now, but it's a good idea to add it in the future. Adding it now to bypass eslint
          ))}
        </Styled.CallToActions>
      )
    }

    return callToActions
  }

  return (
    <>
      {showTopBar && (
        <Styled.TopBar
          reverseTopBar={reverseTopBar}
          className="top-bar"
          headerBackgroundColor={headerBgColor}
          headerTextColor={headerColor}
          topBarBackgroundColor={topBarBgColor}
          topBarTextColor={topBarColor}
        >
          {/* Div used to keep flex content in proper location if hideLocation is used */}
          <div>
            {!hideLocation && (
              <Styled.LocationChanger className="location-changer">
                <LocationOn size="16px" className="location-on" />{' '}
                {userLocation?.city && userLocation?.state?.fullName && (
                  <>
                    {userLocation?.city}, {userLocation?.state?.fullName}
                  </>
                )}
                <Styled.ChildContainer
                  headerTextColor={headerColor}
                  topBarTextColor={topBarColor}
                >
                  {children}
                </Styled.ChildContainer>
              </Styled.LocationChanger>
            )}
          </div>
          {topBarText && <Styled.TopBarText>{topBarText}</Styled.TopBarText>}
          <Styled.TopNav
            reverseTopBar={reverseTopBar}
            headerTextColor={headerColor}
            topBarTextColor={topBarColor}
            className="top-nav"
          >
            {topBarNavData?.map((item, index) => (
              <Link
                className="header-link"
                key={`${item.text}-${index}`} // eslint-disable-line react/no-array-index-key
                to={item?.link || item.href}
                onClick={
                  () =>
                    HEADER.LINKS.handleClick(item.text, item.href, requestId)
                  // eslint-disable-next-line react/jsx-curly-newline
                }
              >
                {item.text}
              </Link>
            ))}
          </Styled.TopNav>
        </Styled.TopBar>
      )}
      <Styled.Header
        role="banner"
        {...props}
        className="leshen-header leshen-header-desktop"
        headerBackgroundColor={headerBgColor}
        headerTextColor={headerColor}
        navUnderLogo={navUnderLogo}
        navigationVisibility={navigationVisibility?.toLowerCase()}
      >
        {navUnderLogo && (
          <>
            <Styled.BottomBar
              className="bottom-bar"
              navUnderLogo={navUnderLogo}
              navigationBackgroundColor={navigationBgColor}
              navigationTextColor={navigationColor}
              headerBackgroundColor={headerBgColor}
            >
              <Link to={logoLink}>{logo}</Link>
              {textBlock && (
                <Styled.TextBlock
                  navUnderLogo={navUnderLogo}
                  textBlockAlignment={textBlockAlignment}
                  headerBackgroundColor={headerBgColor}
                  headerTextColor={headerColor}
                  topBarBackgroundColor={topBarBgColor}
                  topBarTextColor={topBarColor}
                >
                  {textBlock}
                </Styled.TextBlock>
              )}
              {callToActions && (
                // if textBlock is present, we need to pass textBlock prop to right align the callToActions
                <Styled.CallToActions textBlock={hasTextBlock}>
                  {renderCallToActions()}
                </Styled.CallToActions>
              )}
            </Styled.BottomBar>
            <Styled.MainNav
              role="navigation"
              className="main-nav"
              navUnderLogo={navUnderLogo}
              headerBackgroundColor={headerBgColor}
              headerTextColor={headerColor}
              navigationBackgroundColor={navigationBgColor}
              navigationTextColor={navigationColor}
              hoverBackgroundColor={hoverBgColor}
              hoverTextColor={hoverColor}
            >
              {navData?.length > 0 && (
                <RenderNavItems
                  className="render-nav-items"
                  navData={navData}
                  Link={Link}
                />
              )}
            </Styled.MainNav>
          </>
        )}
        {!navUnderLogo && (
          <Styled.BottomBar
            className="bottom-bar"
            navigationTextColor={navigationTextColor}
            headerBackgroundColor={headerBgColor}
            navigationBackgroundColor={navigationBgColor}
            navUnderLogo={navUnderLogo}
          >
            <Styled.MainNav
              role="navigation"
              className="main-nav"
              navUnderLogo={navUnderLogo}
              headerBackgroundColor={headerBgColor}
              headerTextColor={headerColor}
              navigationBackgroundColor={navigationBgColor}
              navigationTextColor={navigationColor}
              hoverBackgroundColor={hoverBgColor}
              hoverTextColor={hoverColor}
            >
              <Link to={logoLink}>{logo}</Link>
              {navData?.length > 0 && (
                <RenderNavItems
                  className="render-nav-items"
                  navData={navData}
                  Link={Link}
                />
              )}
            </Styled.MainNav>
            {textBlock && (
              <Styled.TextBlock
                textBlockAlignment={textBlockAlignment}
                headerBackgroundColor={headerBgColor}
                headerTextColor={headerColor}
              >
                {textBlock}
              </Styled.TextBlock>
            )}
            {callToActions && (
              <Styled.CallToActions textBlock={hasTextBlock}>
                {renderCallToActions()}
              </Styled.CallToActions>
            )}
          </Styled.BottomBar>
        )}
      </Styled.Header>
      <>
        <Styled.MobileHeader
          headerBackgroundColor={headerBgColor}
          headerTextColor={headerColor}
          navigationBackgroundColor={navigationBgColor}
          navigationTextColor={navigationColor}
          navigationVisibility={navigationVisibility?.toLowerCase()}
          className="leshen-header leshen-header-mobile"
        >
          <Link to={logoLink}>{logo}</Link>
          <Styled.MobileMenuButton
            className="menu-button"
            navigationBackgroundColor={headerBgColor}
            navigationTextColor={headerColor}
          >
            {getMobileMenuButton()}
          </Styled.MobileMenuButton>
        </Styled.MobileHeader>
        {isMobileNavOpen && (
          <MobileNav
            className="leshen-header-mobile-nav"
            navData={navData}
            headerBackgroundColor={headerBgColor}
            headerTextColor={headerColor}
            isMobileNavOpen={isMobileNavOpen}
            navigationBackgroundColor={navigationBgColor}
            navigationTextColor={navigationColor}
            hoverBackgroundColor={hoverBgColor}
            hoverTextColor={hoverColor}
            navigationVisibility={navigationVisibility?.toLowerCase()}
          />
        )}
      </>
      <Styled.Border borderColor={border} borderHeight={borderHeight} />
    </>
  )
}

export default Header
